import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Deterministic Tag Mill mobile drawer.
 * Horizon's default drawer relies on native <summary> toggling the `open` attribute
 * while JS adds `.menu-open` on the next frame. Some Android Chrome devices can flicker
 * between those states, so this component owns both states together.
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#bindSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#summary?.removeEventListener('click', this.#onSummaryClick, true);
    this.#summary?.removeEventListener('keydown', this.#onSummaryKeyDown, true);
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#settleTimerLate);
  }

  /** @type {HTMLElement | null} */
  #summary = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimerLate;

  #bindSummary() {
    this.#summary = this.refs.details.querySelector('summary');
    if (!this.#summary) return;

    this.#summary.addEventListener('click', this.#onSummaryClick, true);
    this.#summary.addEventListener('keydown', this.#onSummaryKeyDown, true);
  }

  #onSummaryClick = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.toggle();
  };

  #onSummaryKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.toggle();
  };

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open') || this.refs.details.classList.contains('menu-open');
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * CSS transitions on the drawer panel can freeze when the parent <details>
   * toggles mid-flight: Chrome skips rendering inside a closed <details>
   * subtree, stranding in-flight transitions at stale values. Symptoms: the
   * drawer staying visible for seconds after close (Android), or reopening
   * with correct attributes but an invisible panel. Cancelling the panel's
   * animations (plus a forced reflow) snaps it to the cascade's end state for
   * the current attributes.
   */
  #settlePanel() {
    const panel = this.refs.menuDrawer;
    if (!panel || typeof panel.getAnimations !== 'function') return;
    panel.getAnimations().forEach((animation) => animation.cancel());
    void panel.offsetWidth; // force style/layout recalc so the snap renders
  }

  /**
   * Guarantee the panel reaches its end state after the 0.2s transition should
   * have finished. Runs twice (250ms and 600ms) because a settle can itself
   * land while the subtree is still render-locked during rapid toggling.
   */
  #schedulePanelSettle() {
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#settleTimerLate);
    this.#settleTimer = setTimeout(() => this.#settlePanel(), 250);
    this.#settleTimerLate = setTimeout(() => this.#settlePanel(), 600);
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    if (details === this.refs.details) this.#settlePanel();

    details.setAttribute('open', '');
    details.classList.add('menu-open');
    summary.setAttribute('aria-expanded', 'true');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    if (details === this.refs.details) this.#schedulePanelSettle();

    this.preventInitialAccordionAnimations(details);
    trapFocus(details);
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    if (details === this.refs.details) this.#settlePanel();

    summary.setAttribute('aria-expanded', 'false');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    details.classList.remove('menu-open');
    details.removeAttribute('open');
    removeTrapFocus();

    if (details === this.refs.details) this.#schedulePanelSettle();

    const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
    openDetails.forEach(reset);
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/* === Tag Mill: mobile strip tap opens drawer pre-expanded at that section ===
 *
 * Tapping a category in the mobile strip (.tm-mobile-primary-nav) opens the
 * menu drawer with that category's accordion already expanded, instead of
 * navigating straight to the collection.
 *
 * - Strip links and drawer sections render from the same menu linklist, so
 *   they are matched by their visible titles (identical by construction).
 * - Strip links with no matching drawer section (Shop all, Our Story) keep
 *   their normal navigation.
 * - The drawer menu has no parent-collection links of its own, so a
 *   "Shop all <Category>" link pointing at the strip link's collection is
 *   injected as the first item of each matched section.
 * - Modified clicks (cmd/ctrl/shift/middle) and any JS failure fall back to
 *   plain navigation: the strip links keep their hrefs.
 */
(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const STRIP_LINK_SELECTOR = '.tm-mobile-primary-nav__link';

  const normalise = (text) => (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

  /** Find the drawer accordion <details> whose summary title matches the given title. */
  function findSection(drawer, title) {
    for (const section of drawer.querySelectorAll('accordion-custom details')) {
      const label = section.querySelector('summary .menu-drawer__menu-item-text') || section.querySelector('summary');
      if (label && normalise(label.textContent) === title) return section;
    }
    return null;
  }

  /** Ensure the section's child list starts with a "Shop all <Category>" link. */
  function ensureShopAllLink(section, href, title) {
    const list = section.querySelector('ul.menu-drawer__menu--childlist');
    if (!list || list.querySelector('[data-tm-shop-all]')) return;

    const item = document.createElement('li');
    item.className = 'menu-drawer__list-item';
    item.setAttribute('data-tm-shop-all', '');

    const link = document.createElement('a');
    link.href = href;
    link.className = 'menu-drawer__menu-item menu-drawer__menu-item--child focus-inset';

    const text = document.createElement('span');
    text.className = 'menu-drawer__menu-item-text wrap-text';
    text.textContent = 'Shop all ' + title;

    link.appendChild(text);
    item.appendChild(link);
    list.prepend(item);
  }

  document.addEventListener('click', (event) => {
    if (!MOBILE.matches) return;
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const stripLink = event.target.closest(STRIP_LINK_SELECTOR);
    if (!stripLink) return;

    const drawer = document.querySelector('header-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    const section = findSection(drawer, normalise(stripLink.textContent));
    if (!section) return; // No submenu for this link — navigate as normal.

    event.preventDefault();

    ensureShopAllLink(section, stripLink.href, stripLink.textContent.trim());

    // Close other sections, then open the tapped one.
    drawer.querySelectorAll('accordion-custom details[open]').forEach((other) => {
      if (other !== section) {
        other.removeAttribute('open');
        other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }
    });
    section.setAttribute('open', '');
    section.querySelector('summary')?.setAttribute('aria-expanded', 'true');

    drawer.open();

    requestAnimationFrame(() => {
      section.scrollIntoView({ block: 'nearest' });
    });
  });
})();
